import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import axios from "axios";

import {
  myApi,
  defaultPadding,
  cardMaxWidth,
  cardBorderRadius,
  bgGradient,
  buttonGradient,
  buttonGradientHover,
  cardShadow,
  buttonShadow,
  buttonShadowHover,
  STORAGE_KEYS,
  MESSAGES,
} from "../../constants/consts";

// --- Animasyonlar ---
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const logoIn = keyframes`
  0%   { opacity: 0; transform: scale(0.85) translateY(-10px); }
  60%  { opacity: 1; transform: scale(1.03) translateY(0); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
`;
const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-6px); }
  40%      { transform: translateX(6px); }
  60%      { transform: translateX(-4px); }
  80%      { transform: translateX(4px); }
`;

export default function Login({ onLoginSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // Hata değişince shake tetiklensin diye key kullanıyoruz
  const [errorKey, setErrorKey] = useState(0);

  // --- Şifremi unuttum ---
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotUser, setForgotUser] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState({ type: "", text: "" });

  const openForgot = (e) => {
    e.preventDefault();
    setForgotUser(username.trim());
    setForgotMsg({ type: "", text: "" });
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotUser.trim()) {
      setForgotMsg({
        type: "error",
        text: "Lütfen kullanıcı adınızı giriniz.",
      });
      return;
    }
    setForgotLoading(true);
    setForgotMsg({ type: "", text: "" });
    try {
      const res = await axios.post(`${myApi}/forgot_password`, {
        username: forgotUser.trim(),
      });
      setForgotMsg({
        type: "success",
        text:
          res.data?.message ||
          "Kullanıcı adınıza bağlı e-posta adresine yeni şifreniz gönderildi.",
      });
    } catch (err) {
      setForgotMsg({
        type: "error",
        text: err.response?.data?.error || MESSAGES.CONNECTION_ERROR,
      });
    } finally {
      setForgotLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!username.trim() || !password) {
      setError(MESSAGES.REQUIRED_FIELDS);
      setErrorKey((k) => k + 1);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${myApi}/login`, {
        username: username.trim(),
        password: password,
        token: "",
      });

      const { user_id, user_type, sunucu_kodu, message } = response.data;

      setSuccess(message || MESSAGES.SUCCESS_DEFAULT);

      const userData = {
        user_id,
        user_type,
        sunucu_kodu,
        username: username.trim(),
        panel: "dashboard",
      };

      if (rememberMe) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        localStorage.setItem(STORAGE_KEYS.USERNAME, username.trim());
        localStorage.setItem(STORAGE_KEYS.SUNUCU_KODU, sunucu_kodu || "");
        localStorage.setItem(STORAGE_KEYS.USER_TYPE, user_type || "");
      } else {
        sessionStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
        sessionStorage.setItem(STORAGE_KEYS.USERNAME, username.trim());
        sessionStorage.setItem(STORAGE_KEYS.SUNUCU_KODU, sunucu_kodu || "");
        sessionStorage.setItem(STORAGE_KEYS.USER_TYPE, user_type || "");
      }

      if (onLoginSuccess) onLoginSuccess(userData);
    } catch (err) {
      const msg = err.response?.data?.message || MESSAGES.CONNECTION_ERROR;
      setError(msg);
      setErrorKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        height: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        background: bgGradient,
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          width: { xs: 220, sm: 320, md: 400 },
          height: { xs: 220, sm: 320, md: 400 },
          borderRadius: "50%",
          background: "rgba(255,255,255,0.10)",
          top: -100,
          right: -100,
          pointerEvents: "none",
          animation: `${fadeIn} 1.2s ease-out both`,
        },
        "&::after": {
          content: '""',
          position: "absolute",
          width: { xs: 160, sm: 240, md: 300 },
          height: { xs: 160, sm: 240, md: 300 },
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          bottom: -80,
          left: -80,
          pointerEvents: "none",
          animation: `${fadeIn} 1.4s ease-out 0.15s both`,
        },
      }}
    >
      {/* Logo — pop efektiyle gelir */}
      <Box
        sx={{
          pt: { xs: 2, sm: 4, md: 5 },
          // pb: { xs: 1, sm: 3 },
          flexShrink: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 1,
          animation: `${logoIn} 0.7s cubic-bezier(0.22, 1, 0.36, 1) both`,
        }}
      >
        <Box
          component="img"
          src="/images/logo2.png"
          alt="Nortek Logo"
          sx={{
            height: { xs: 48, sm: 60, md: 72 },
            width: "auto",
            objectFit: "contain",
            filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.20))",
            transition: "transform 0.4s ease, filter 0.4s ease",
            "&:hover": {
              transform: "scale(1.05)",
              filter: "drop-shadow(0 10px 24px rgba(0,0,0,0.28))",
            },
          }}
        />
      </Box>

      {/* Kart */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "center",
          px: { xs: 2, sm: 3, md: `${defaultPadding}px` },
          pt: { xs: 12, sm: 0 },
          pb: { xs: 3, sm: 4, md: 5 },
          zIndex: 1,
          overflowY: "auto",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 420, md: cardMaxWidth },
            borderRadius: { xs: 2, sm: `${cardBorderRadius}px` },
            boxShadow: cardShadow,
            backdropFilter: "blur(10px)",
            animation: `${fadeInUp} 0.6s cubic-bezier(0.22, 1, 0.36, 1) 0.15s backwards`,
          }}
        >
          <CardContent sx={{ padding: { xs: 2.5, sm: 3.5, md: 4 } }}>
            {/* Başlık */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: { xs: 2.5, sm: 3 },
                animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.3s backwards`,
              }}
            >
              <Typography
                variant="h4"
                fontWeight="700"
                color="text.primary"
                gutterBottom
                sx={{
                  fontSize: { xs: "1.4rem", sm: "1.75rem", md: "2rem" },
                  textAlign: "center",
                }}
              >
                {MESSAGES.LOGIN_TITLE}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: "center",
                  fontSize: { xs: "0.8rem", sm: "0.875rem" },
                }}
              >
                {MESSAGES.LOGIN_SUBTITLE}
              </Typography>
            </Box>

            {/* Hata / Başarı — hata gelince shake */}
            {error && (
              <Alert
                key={errorKey}
                severity="error"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  fontSize: { xs: 13, sm: 14 },
                  animation: `${shake} 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97) both, ${fadeIn} 0.25s ease-out both`,
                }}
              >
                {error}
              </Alert>
            )}
            {success && (
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  fontSize: { xs: 13, sm: 14 },
                  animation: `${fadeInUp} 0.35s ease-out both`,
                }}
              >
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Username */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.4s backwards`,
                }}
              >
                <TextField
                  fullWidth
                  label={MESSAGES.USERNAME_LABEL}
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  autoComplete="username"
                  sx={{
                    mb: 2,
                    "& .MuiOutlinedInput-root": {
                      transition:
                        "box-shadow 0.25s ease, transform 0.25s ease, background-color 0.25s ease",
                      "&:hover": {
                        backgroundColor: "rgba(38,151,255,0.02)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(38,151,255,0.12)",
                        transform: "translateY(-1px)",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlined
                          color="action"
                          sx={{
                            transition: "transform 0.25s ease",
                            ".Mui-focused &": { transform: "scale(1.1)" },
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Password */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.48s backwards`,
                }}
              >
                <TextField
                  fullWidth
                  label={MESSAGES.PASSWORD_LABEL}
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  sx={{
                    mb: 1,
                    "& .MuiOutlinedInput-root": {
                      transition:
                        "box-shadow 0.25s ease, transform 0.25s ease, background-color 0.25s ease",
                      "&:hover": {
                        backgroundColor: "rgba(38,151,255,0.02)",
                      },
                      "&.Mui-focused": {
                        boxShadow: "0 0 0 4px rgba(38,151,255,0.12)",
                        transform: "translateY(-1px)",
                      },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined
                          color="action"
                          sx={{
                            transition: "transform 0.25s ease",
                            ".Mui-focused &": { transform: "scale(1.1)" },
                          }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          disabled={loading}
                          size="small"
                          sx={{
                            transition: "transform 0.2s ease",
                            "&:hover": { transform: "scale(1.1)" },
                            "&:active": { transform: "scale(0.9)" },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Remember me + Forgot */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  gap: { xs: 0.5, sm: 0 },
                  mb: { xs: 2, sm: 3 },
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.56s backwards`,
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      disabled={loading}
                      sx={{
                        transition: "transform 0.2s ease",
                        "&:active": { transform: "scale(0.9)" },
                      }}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                    >
                      {MESSAGES.REMEMBER_ME}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
                <Link
                  href="#"
                  onClick={openForgot}
                  underline="hover"
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    transition: "transform 0.2s ease",
                    display: "inline-block",
                    "&:hover": { transform: "translateX(2px)" },
                  }}
                >
                  {MESSAGES.FORGOT_PASSWORD}
                </Link>
              </Box>

              {/* Buton */}
              <Box
                sx={{
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.64s backwards`,
                }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: { xs: "0.95rem", sm: "1rem" },
                    background: buttonGradient,
                    boxShadow: buttonShadow,
                    transition:
                      "transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease",
                    "&:hover": {
                      background: buttonGradientHover,
                      boxShadow: buttonShadowHover,
                      transform: "translateY(-2px)",
                    },
                    "&:active": { transform: "translateY(0) scale(0.98)" },
                    "&:disabled": { background: "#c7c7c7" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={24} sx={{ color: "#fff" }} />
                  ) : (
                    MESSAGES.LOGIN_BUTTON
                  )}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={forgotOpen}
        onClose={() => !forgotLoading && setForgotOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <form onSubmit={handleForgotSubmit}>
          <DialogTitle sx={{ fontWeight: 700 }}>Şifremi unuttum</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
              Kullanıcı adınızı girin. Hesabınıza kayıtlı e-posta adresine yeni
              bir şifre göndereceğiz.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label={MESSAGES.USERNAME_LABEL}
              value={forgotUser}
              onChange={(e) => setForgotUser(e.target.value)}
              disabled={forgotLoading || forgotMsg.type === "success"}
            />
            {forgotMsg.text && (
              <Alert severity={forgotMsg.type || "info"} sx={{ mt: 2 }}>
                {forgotMsg.text}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setForgotOpen(false)}
              disabled={forgotLoading}
              sx={{ textTransform: "none" }}
            >
              {forgotMsg.type === "success" ? "Kapat" : "Vazgeç"}
            </Button>
            {forgotMsg.type !== "success" && (
              <Button
                type="submit"
                variant="contained"
                disabled={forgotLoading}
                sx={{ textTransform: "none", background: buttonGradient }}
              >
                {forgotLoading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Gönder"
                )}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}
